import React, { useState, useEffect } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { Chrome, Puzzle, AlertTriangle, ExternalLink, X } from 'lucide-react'

const SystemRequirements = () => {
    const [isChrome, setIsChrome] = useState(true)
    const [hasExtension, setHasExtension] = useState(true)
    const [showBanner, setShowBanner] = useState(true)

    useEffect(() => {
        // Check if browser is Chrome (or Chromium-based)
        const checkBrowser = () => {
            const isChromium = !!window.chrome
            const winNav = window.navigator
            const vendorName = winNav.vendor
            const isOpera = typeof window.opr !== "undefined"
            const isIEedge = winNav.userAgent.indexOf("Edg") > -1
            const isIOSChrome = winNav.userAgent.match("CriOS")

            if (isIOSChrome) {
                setIsChrome(true)
            } else if (
                isChromium !== null &&
                typeof isChromium !== "undefined" &&
                vendorName === "Google Inc." &&
                isOpera === false &&
                isIEedge === false
            ) {
                setIsChrome(true)
            } else {
                // Allow if it detects the extension anyway (e.g. Edge with Chrome extensions allowed)
                setIsChrome(false)
            }
        }

        // Check for Extension
        const checkExtension = () => {
            // Give it a moment for content script to inject
            setTimeout(() => {
                if (window.__EZ_CONTROL_EXTENSION_ACTIVE__) {
                    setHasExtension(true)
                    // If extension is active, we can assume browser is supported enough
                    setIsChrome(true)
                } else {
                    setHasExtension(false)
                }
            }, 1000)
        }

        checkBrowser()
        checkExtension()
    }, [])

    if (!showBanner) return null

    // If everything is fine, don't show anything
    if (isChrome && hasExtension) return null

    return (
        <AnimatePresence>
            <motion.div
                initial={{ opacity: 0, y: -50 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -50 }}
                className="fixed top-20 right-6 z-50 max-w-md w-full"
            >
                <div className="bg-slate-800/90 backdrop-blur-md border border-yellow-500/50 rounded-xl shadow-2xl overflow-hidden">
                    <div className="p-4">
                        <div className="flex items-start justify-between">
                            <div className="flex items-center space-x-2 text-yellow-400 mb-2">
                                <AlertTriangle className="w-5 h-5" />
                                <h3 className="font-bold">System Requirements</h3>
                            </div>
                            <button
                                onClick={() => setShowBanner(false)}
                                className="text-gray-400 hover:text-white transition-colors"
                            >
                                <X className="w-5 h-5" />
                            </button>
                        </div>

                        <div className="space-y-4">
                            {/* Browser Check */}
                            {!isChrome && (
                                <div className="flex items-start space-x-3 bg-white/5 p-3 rounded-lg">
                                    <div className="bg-red-500/20 p-2 rounded-full text-red-400">
                                        <Chrome className="w-5 h-5" />
                                    </div>
                                    <div>
                                        <h4 className="font-medium text-white">Google Chrome Required</h4>
                                        <p className="text-sm text-gray-400 mt-1">
                                            This app requires Google Chrome features to communicate with local devices reliably.
                                        </p>
                                        <a
                                            href="https://www.google.com/chrome/"
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="inline-flex items-center space-x-1 text-sm text-blue-400 hover:text-blue-300 mt-2 font-medium"
                                        >
                                            <span>Download Chrome</span>
                                            <ExternalLink className="w-3 h-3" />
                                        </a>
                                    </div>
                                </div>
                            )}

                            {/* Extension Check */}
                            {(!hasExtension) && (
                                <div className="flex items-start space-x-3 bg-white/5 p-3 rounded-lg">
                                    <div className="bg-orange-500/20 p-2 rounded-full text-orange-400">
                                        <Puzzle className="w-5 h-5" />
                                    </div>
                                    <div>
                                        <h4 className="font-medium text-white">Ez-Control Bridge Missing</h4>
                                        <p className="text-sm text-gray-400 mt-1">
                                            To control devices on your local network from this secure site, you must install our Chrome Extension.
                                        </p>
                                        <div className="mt-3 bg-black/30 p-2 rounded border border-white/10 text-xs font-mono text-gray-300">
                                            chrome://extensions &gt; Load Unpacked
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>

                    <div className="bg-yellow-500/10 px-4 py-3 border-t border-yellow-500/20">
                        <p className="text-xs text-yellow-200/80 text-center">
                            Limited functionality available without these requirements.
                        </p>
                    </div>
                </div>
            </motion.div>
        </AnimatePresence>
    )
}

export default SystemRequirements
